import { useEffect, useState, useCallback } from "react";
import { toast } from "sonner";
import { Building2, Loader2, Save, ChevronDown, Wrench, Zap, Crown, Plus, RefreshCw, AlertTriangle } from "lucide-react";
import api from "@/lib/api";
import { useReloadBus } from "@/lib/reloadBus";
import { AppHeader } from "@/components/AppHeader";

const PARTNER_TYPE_ICONS = {
  call_center: "📞",
  vertrieb: "💼",
  installateur: "🔧",
  full_service: "🏢",
  custom: "⚙️",
};

const inputCls = "h-9 w-full bg-[#09090B] border border-zinc-700 rounded-lg px-3 text-zinc-50 focus:border-[#00E575] outline-none text-[12.5px]";

export default function AdminPartnerProfile() {
  const [tenants, setTenants] = useState([]);
  const [registry, setRegistry] = useState(null);
  const [selectedTenant, setSelectedTenant] = useState("");
  const [profile, setProfile] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [showNewWizard, setShowNewWizard] = useState(false);

  const load = useCallback(async (silent = false) => {
    if (!silent) setLoading(true);
    try {
      const [{ data: regData }, { data: tList }] = await Promise.all([
        api.get("/superadmin/adapters/registry"),
        api.get("/superadmin/tenants"),
      ]);
      setRegistry(regData);
      setTenants((tList || []).filter((t) => t.active !== false));
    } catch (e) {
      toast.error(e.response?.data?.detail || "Laden fehlgeschlagen");
    } finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);
  // Iter88zbu — Silent-Reload nach jeder Mutation.
  useReloadBus("*", () => load(true));

  useEffect(() => {
    if (!selectedTenant) { setProfile(null); return; }
    api.get(`/superadmin/tenants/${selectedTenant}/profile`)
      .then((r) => setProfile(r.data))
      .catch((e) => toast.error(e.response?.data?.detail || "Profil laden fehlgeschlagen"));
  }, [selectedTenant]);

  const applyPreset = async (newType, resetAdapters) => {
    if (!selectedTenant) return;
    // Confirm on existing tenants — clicking a preset rewrites capabilities + adapters
    if (profile && profile.partner_type !== newType) {
      const confirmText = `Mandanten-Typ wirklich auf "${newType}" wechseln?\n\nDie Capabilities werden auf das Preset zurückgesetzt. Ihre individuellen Anpassungen gehen verloren.`;
      if (!window.confirm(confirmText)) return;
    }
    setSaving(true);
    try {
      const { data } = await api.post(`/superadmin/tenants/${selectedTenant}/apply-preset`, {
        partner_type: newType, reset_adapters: !!resetAdapters,
      });
      setProfile(data);
      toast.success(`Preset '${newType}' angewendet`);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Anwenden fehlgeschlagen");
    } finally { setSaving(false); }
  };

  const toggleCap = async (cap) => {
    if (!profile) return;
    const current = new Set(profile.capabilities);
    if (current.has(cap)) current.delete(cap); else current.add(cap);
    setSaving(true);
    try {
      const { data } = await api.put(`/superadmin/tenants/${selectedTenant}/profile`, {
        partner_type: profile.partner_type,
        capabilities: Array.from(current),
        adapters: profile.adapters,
      });
      setProfile(data);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Speichern fehlgeschlagen");
    } finally { setSaving(false); }
  };

  const setAdapter = async (slot, kind, config) => {
    setSaving(true);
    try {
      const { data } = await api.patch(`/superadmin/tenants/${selectedTenant}/adapter/${slot}`, {
        kind, config: config || {},
      });
      setProfile(data);
      toast.success("Adapter aktualisiert");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Adapter-Update fehlgeschlagen");
    } finally { setSaving(false); }
  };

  return (
    <div className="min-h-screen bg-[#09090B]" data-testid="admin-partner-profile">
      <AppHeader title="Mandanten-Profile" back="/admin" />
      <main className="max-w-6xl mx-auto px-3 sm:px-4 py-4 sm:py-6">
        <div className="flex flex-wrap items-center justify-between gap-2 mb-4">
          <div>
            <h1 className="font-display text-xl sm:text-2xl font-bold flex items-center gap-2">
              <Building2 className="text-[#00E575]" size={22} /> Kooperationspartner & Adapter
            </h1>
            <p className="text-[12px] text-zinc-300 font-medium">Mandanten-Typ + Capabilities + 5 Adapter-Slots pro Mandant konfigurieren</p>
          </div>
          <div className="flex gap-2">
            <button onClick={load} data-testid="profile-refresh" className="h-9 px-3 rounded-lg bg-zinc-800 hover:bg-zinc-700 text-zinc-300 text-[12px] inline-flex items-center gap-1.5 font-medium">
              <RefreshCw size={14} /> Neu laden
            </button>
            <button onClick={() => setShowNewWizard(true)} data-testid="profile-new-partner" className="h-9 px-3 rounded-lg bg-[#00E575] hover:bg-[#00C964] text-[#09090B] font-bold text-[12px] inline-flex items-center gap-1.5">
              <Plus size={14} /> Neuer Partner
            </button>
          </div>
        </div>

        {loading ? (
          <div className="flex items-center justify-center py-12"><Loader2 className="animate-spin text-zinc-500" size={28} /></div>
        ) : (
          <div className="grid md:grid-cols-[260px_1fr] gap-4">
            {/* Tenant List */}
            <aside className="space-y-1.5">
              <div className="text-[12px] uppercase tracking-widest text-zinc-300 font-semibold font-bold mb-1">Mandant wählen</div>
              {tenants.map((t) => {
                const isSel = t.id === selectedTenant;
                return (
                  <button
                    key={t.id}
                    data-testid={`tenant-pick-${t.slug}`}
                    onClick={() => setSelectedTenant(t.id)}
                    className={`w-full text-left px-3 py-2 rounded-lg border transition-colors ${
                      isSel ? "border-[#00E575]/60 bg-[#00E575]/10" : "border-zinc-800 bg-[#18181B] hover:border-zinc-600"
                    }`}
                  >
                    <div className="text-[13px] font-bold flex items-center gap-1.5">
                      <span>{PARTNER_TYPE_ICONS[t.partner_type] || "📋"}</span>
                      <span className="truncate">{t.name}</span>
                    </div>
                    <div className="text-[10.5px] text-zinc-500 font-mono">{t.slug}</div>
                  </button>
                );
              })}
            </aside>

            {/* Profile editor */}
            <section className="space-y-4">
              {!profile ? (
                <div className="bg-[#18181B] border border-zinc-800 rounded-2xl p-8 text-center text-zinc-300 text-sm font-medium">
                  Wähle links einen Mandanten zur Konfiguration.
                </div>
              ) : (
                <>
                  {/* Partner-Type Picker */}
                  <div className="bg-[#18181B] border border-zinc-800 rounded-2xl p-4">
                    <div className="text-[12px] uppercase tracking-widest text-zinc-300 font-semibold font-bold mb-2">Mandanten-Typ (Preset)</div>
                    <div className="flex flex-wrap gap-2">
                      {registry.partner_types.map((pt) => {
                        const sel = profile.partner_type === pt.kind;
                        return (
                          <button
                            key={pt.kind}
                            onClick={() => applyPreset(pt.kind, false)}
                            disabled={saving}
                            data-testid={`partner-type-${pt.kind}`}
                            className={`px-3 h-9 rounded-lg border text-[12px] font-medium inline-flex items-center gap-1.5 ${
                              sel ? "border-[#00E575]/60 bg-[#00E575]/10 text-[#00E575]" : "border-zinc-700 bg-[#09090B] text-zinc-300 hover:border-zinc-500"
                            }`}
                          >
                            <span>{PARTNER_TYPE_ICONS[pt.kind]}</span> {pt.label}
                          </button>
                        );
                      })}
                    </div>
                    {profile.is_custom_capabilities && (
                      <p className="mt-2 text-[10.5px] text-amber-300 flex items-center gap-1.5">
                        <AlertTriangle size={10} /> Capabilities sind individuell angepasst — weichen vom Preset ab.
                      </p>
                    )}
                  </div>

                  {/* Capabilities */}
                  <div className="bg-[#18181B] border border-zinc-800 rounded-2xl p-4">
                    <div className="flex items-center justify-between mb-2">
                      <div className="text-[12px] uppercase tracking-widest text-zinc-300 font-semibold font-bold">Capabilities ({profile.capabilities.length})</div>
                      <button
                        onClick={() => applyPreset(profile.partner_type, false)}
                        className="text-[10.5px] text-zinc-500 hover:text-zinc-200"
                        data-testid="cap-reset-preset"
                      >
                        Auf Preset zurücksetzen
                      </button>
                    </div>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-1.5">
                      {registry.capabilities.map((c) => {
                        const enabled = profile.capabilities.includes(c.kind);
                        return (
                          <label
                            key={c.kind}
                            data-testid={`cap-toggle-${c.kind}`}
                            className={`flex items-center gap-2 px-2.5 py-2 rounded-lg border cursor-pointer ${
                              enabled ? "border-[#00E575]/40 bg-[#00E575]/5" : "border-zinc-800 bg-[#09090B] hover:border-zinc-600"
                            }`}
                          >
                            <input type="checkbox" checked={enabled} onChange={() => toggleCap(c.kind)} disabled={saving} className="accent-[#00E575]" />
                            <span className="text-[12px] text-zinc-200 flex-1 font-medium">{c.label}</span>
                            <code className="text-[9.5px] text-zinc-500 font-mono">{c.kind}</code>
                          </label>
                        );
                      })}
                    </div>
                  </div>

                  {/* Adapter Slots */}
                  <div className="bg-[#18181B] border border-zinc-800 rounded-2xl p-4">
                    <div className="text-[12px] uppercase tracking-widest text-zinc-300 font-semibold font-bold mb-3">Adapter-Slots (5)</div>
                    <div className="space-y-3">
                      {registry.adapter_slots.map((slot) => (
                        <AdapterSlotEditor
                          key={slot}
                          slot={slot}
                          options={registry.adapters[slot]}
                          current={profile.adapters[slot]}
                          onChange={(kind, config) => setAdapter(slot, kind, config)}
                          disabled={saving}
                        />
                      ))}
                    </div>
                  </div>
                </>
              )}
            </section>
          </div>
        )}

        {/* New Partner Wizard */}
        {showNewWizard && (
          <NewPartnerWizard
            registry={registry}
            onClose={() => setShowNewWizard(false)}
            onCreated={(tid) => { setShowNewWizard(false); load(); setTimeout(() => setSelectedTenant(tid), 400); }}
          />
        )}
      </main>
    </div>
  );
}

// ───────────────────────────────────────────────────────────────────────────

const SLOT_LABEL = {
  address_source: "Adress-Quelle",
  dialer:         "Telefonie / Dialer",
  pricing:        "Abrechnung / Pricing",
  recording:      "Aufzeichnung",
  lead_export:    "Lead-Export",
};

function AdapterSlotEditor({ slot, options, current, onChange, disabled }) {
  const [open, setOpen] = useState(false);
  const [kind, setKind] = useState(current?.kind || options[0].kind);
  const [config, setConfig] = useState(current?.config || {});
  const activeOpt = options.find((o) => o.kind === kind);
  const labelOfCurrent = options.find((o) => o.kind === current?.kind)?.label || current?.kind;

  return (
    <div className="border border-zinc-800 rounded-lg overflow-hidden" data-testid={`adapter-slot-${slot}`}>
      <button onClick={() => setOpen((v) => !v)} className="w-full flex items-center justify-between px-3 py-2 hover:bg-zinc-800/40 transition-colors">
        <div className="text-left">
          <div className="text-[12px] uppercase tracking-widest text-zinc-300 font-bold">{SLOT_LABEL[slot] || slot}</div>
          <div className="text-[13px] text-zinc-100 font-medium">{labelOfCurrent}</div>
        </div>
        <ChevronDown size={14} className={`text-zinc-500 transition-transform ${open ? "rotate-180" : ""}`} />
      </button>
      {open && (
        <div className="border-t border-zinc-800 p-3 bg-[#09090B] space-y-2">
          <select
            value={kind}
            onChange={(e) => { setKind(e.target.value); setConfig({}); }}
            data-testid={`adapter-kind-${slot}`}
            className={inputCls}
          >
            {options.map((o) => <option key={o.kind} value={o.kind}>{o.label}</option>)}
          </select>
          {activeOpt && Object.keys(activeOpt.config_schema || {}).length > 0 && (
            <div className="space-y-2">
              <div className="text-[12px] uppercase tracking-widest text-zinc-300 font-semibold font-bold">Adapter-Config</div>
              {Object.entries(activeOpt.config_schema).map(([key, type]) => (
                <ConfigField
                  key={key}
                  field={key}
                  type={type}
                  value={config[key]}
                  onChange={(v) => setConfig((c) => ({ ...c, [key]: v }))}
                  testid={`cfg-${slot}-${key}`}
                />
              ))}
            </div>
          )}
          <button
            onClick={() => onChange(kind, config)}
            disabled={disabled}
            data-testid={`adapter-save-${slot}`}
            className="w-full h-9 rounded-lg bg-[#00E575] hover:bg-[#00C964] text-[#09090B] font-bold text-[12px] inline-flex items-center justify-center gap-1.5 disabled:bg-zinc-800 disabled:text-zinc-300"
          >
            <Save size={12} /> Adapter speichern
          </button>
        </div>
      )}
    </div>
  );
}

function ConfigField({ field, type, value, onChange, testid }) {
  // Handle one level of nested object schemas (e.g. pricing tiers) inline
  // without recursion (Babel chokes on recursive JSX components).
  if (typeof type === "object") {
    return (
      <div className="space-y-1 pl-2 border-l border-zinc-800">
        <div className="text-[10.5px] text-zinc-500 font-mono">{field}</div>
        {Object.entries(type).map(([subKey, subType]) => {
          const isNum = subType === "float" || subType === "int" || subType === "float?" || subType === "int?";
          const v = value?.[subKey];
          return (
            <div className="flex items-center gap-2" key={subKey}>
              <label className="text-[12px] text-zinc-400 w-32 font-mono truncate">{subKey}</label>
              <input
                type={isNum ? "number" : "text"}
                step={subType === "float" || subType === "float?" ? "0.01" : "1"}
                value={v ?? ""}
                onChange={(e) => onChange({ ...(value || {}), [subKey]: isNum ? (e.target.value === "" ? null : Number(e.target.value)) : e.target.value })}
                data-testid={`${testid}-${subKey}`}
                className={inputCls}
              />
            </div>
          );
        })}
      </div>
    );
  }
  const isNum = type === "float" || type === "int" || type === "float?" || type === "int?";
  return (
    <div className="flex items-center gap-2">
      <label className="text-[12px] text-zinc-400 w-32 font-mono truncate">{field}</label>
      <input
        type={isNum ? "number" : "text"}
        step={type === "float" || type === "float?" ? "0.01" : "1"}
        value={value ?? ""}
        onChange={(e) => onChange(isNum ? (e.target.value === "" ? null : Number(e.target.value)) : e.target.value)}
        data-testid={testid}
        className={inputCls}
      />
    </div>
  );
}

// ───────────────────────────────────────────────────────────────────────────

function NewPartnerWizard({ registry, onClose, onCreated }) {
  const [mode, setMode] = useState("direct"); // direct | invite
  const [step, setStep] = useState(1);
  const [form, setForm] = useState({
    name: "", slug: "", partner_type: "call_center",
    initial_admin_first_name: "", initial_admin_last_name: "",
    initial_admin_email: "", initial_admin_phone: "",
  });
  const [submitting, setSubmitting] = useState(false);
  const [inviteResult, setInviteResult] = useState(null);

  const submitDirect = async () => {
    if (!form.name || !form.slug) return toast.error("Name und Slug sind Pflicht");
    setSubmitting(true);
    try {
      const { data } = await api.post("/superadmin/partners", form);
      toast.success(`Mandant ${data.name} angelegt`);
      onCreated(data.tenant_id);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Anlegen fehlgeschlagen");
    } finally { setSubmitting(false); }
  };

  const submitInvite = async () => {
    if (!form.initial_admin_email) return toast.error("E-Mail des Partner-Admins ist Pflicht");
    setSubmitting(true);
    try {
      const { data } = await api.post("/superadmin/tenant-invitations", {
        partner_type: form.partner_type,
        suggested_name: form.name || null,
        suggested_slug: form.slug || null,
        target_email: form.initial_admin_email,
      });
      setInviteResult(data);
      toast.success(data.delivery?.ok ? "Einladung versendet" : "Einladung erstellt — Link unten");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Einladung fehlgeschlagen");
    } finally { setSubmitting(false); }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/70 flex items-center justify-center p-3" data-testid="new-partner-wizard">
      <div className="w-full max-w-lg bg-[#18181B] border border-zinc-800 rounded-2xl max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between px-5 py-3 border-b border-zinc-800">
          <div>
            <h3 className="font-bold">Neuer Kooperationspartner</h3>
            <p className="text-[10.5px] text-zinc-500">Schritt {step} von 3</p>
          </div>
          <button onClick={onClose} className="p-1 hover:bg-zinc-800 rounded text-zinc-400">✕</button>
        </div>
        <div className="p-5 space-y-3">
          {/* Mode toggle */}
          <div className="flex gap-2 p-1 bg-[#09090B] rounded-lg" data-testid="wizard-mode-switch">
            <button onClick={() => { setMode("direct"); setInviteResult(null); }} className={`flex-1 h-8 rounded-md text-[12px] font-bold ${mode === "direct" ? "bg-[#00E575] text-[#09090B]" : "text-zinc-400"}`} data-testid="wizard-mode-direct">
              Direkt anlegen
            </button>
            <button onClick={() => { setMode("invite"); setInviteResult(null); }} className={`flex-1 h-8 rounded-md text-[12px] font-bold ${mode === "invite" ? "bg-amber-500 text-[#09090B]" : "text-zinc-400"}`} data-testid="wizard-mode-invite">
              Magic-Link senden
            </button>
          </div>
          {inviteResult ? (
            <div className="p-3 rounded-lg bg-amber-500/5 border border-amber-500/30 space-y-2" data-testid="wizard-invite-result">
              <div className="text-[12px] uppercase tracking-widest text-amber-300 font-bold">✓ Einladung erstellt</div>
              <p className="text-[12px] text-zinc-300 font-medium">Der Partner-Admin bekommt eine E-Mail mit Onboarding-Link an <strong>{form.initial_admin_email}</strong>.</p>
              <input readOnly value={inviteResult.invite_url} onFocus={(e) => e.target.select()} className="w-full bg-[#09090B] border border-zinc-700 rounded px-2 py-1.5 text-[10.5px] text-zinc-300 font-mono"/>
              <button onClick={onClose} className="w-full h-9 rounded-lg bg-zinc-800 text-zinc-300 text-[12px] font-medium">Schließen</button>
            </div>
          ) : step === 1 ? (
            <>
              <div>
                <label className="block text-[12px] uppercase tracking-widest text-zinc-200 mb-1 font-bold">Mandanten-Typ *</label>
                <div className="grid grid-cols-2 gap-2">
                  {registry?.partner_types?.filter((p) => p.kind !== "custom").map((p) => (
                    <button
                      key={p.kind}
                      onClick={() => setForm({ ...form, partner_type: p.kind })}
                      data-testid={`wizard-type-${p.kind}`}
                      className={`p-3 rounded-lg border text-left ${form.partner_type === p.kind ? "border-[#00E575]/60 bg-[#00E575]/10" : "border-zinc-800 bg-[#09090B]"}`}
                    >
                      <div className="text-lg">{PARTNER_TYPE_ICONS[p.kind]}</div>
                      <div className="text-[12.5px] font-bold mt-0.5">{p.label}</div>
                    </button>
                  ))}
                </div>
              </div>
              <button onClick={() => setStep(2)} className="w-full h-10 rounded-lg bg-[#00E575] text-[#09090B] font-bold mt-3" data-testid="wizard-next-1">
                Weiter
              </button>
            </>
          ) : step === 2 ? (
            <>
              <div>
                <label className="block text-[12px] uppercase tracking-widest text-zinc-200 mb-1 font-bold">{mode === "invite" ? "Name (Vorschlag, Partner kann ändern)" : "Name *"}</label>
                <input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="Türkei Callcenter GmbH" data-testid="wizard-name" className={inputCls} />
              </div>
              <div>
                <label className="block text-[12px] uppercase tracking-widest text-zinc-200 mb-1 font-bold">{mode === "invite" ? "Slug (Vorschlag)" : "Slug *"}</label>
                <input value={form.slug} onChange={(e) => setForm({ ...form, slug: e.target.value.toLowerCase().replace(/[^a-z0-9-]/g, "-") })} placeholder="tuerkei-2" data-testid="wizard-slug" className={inputCls} />
              </div>
              <div className="flex gap-2 mt-3">
                <button onClick={() => setStep(1)} className="flex-1 h-10 rounded-lg bg-zinc-800 text-zinc-300">Zurück</button>
                <button onClick={() => setStep(3)} disabled={mode === "direct" && (!form.name || !form.slug)} className="flex-1 h-10 rounded-lg bg-[#00E575] text-[#09090B] font-bold disabled:bg-zinc-800 disabled:text-zinc-500" data-testid="wizard-next-2">
                  Weiter
                </button>
              </div>
            </>
          ) : (
            <>
              {mode === "direct" ? (
                <p className="text-[12px] text-zinc-300 font-medium">Optional: Erster Admin-User für diesen Mandanten (Sie können das auch später machen)</p>
              ) : (
                <p className="text-[12px] text-zinc-300 font-medium">E-Mail des Partner-Admins. Er bekommt den Onboarding-Link und richtet seinen Mandanten selbst ein.</p>
              )}
              {mode === "direct" && (
                <div className="grid grid-cols-2 gap-2">
                  <input value={form.initial_admin_first_name} onChange={(e) => setForm({ ...form, initial_admin_first_name: e.target.value })} placeholder="Vorname" data-testid="wizard-admin-first" className={inputCls} />
                  <input value={form.initial_admin_last_name} onChange={(e) => setForm({ ...form, initial_admin_last_name: e.target.value })} placeholder="Nachname" data-testid="wizard-admin-last" className={inputCls} />
                </div>
              )}
              <input value={form.initial_admin_email} onChange={(e) => setForm({ ...form, initial_admin_email: e.target.value })} placeholder={mode === "invite" ? "partner-admin@example.com *" : "admin@partner.de"} data-testid="wizard-admin-email" className={inputCls} />
              {mode === "direct" && (
                <input value={form.initial_admin_phone} onChange={(e) => setForm({ ...form, initial_admin_phone: e.target.value })} placeholder="+49 …" data-testid="wizard-admin-phone" className={inputCls} />
              )}
              <div className="flex gap-2 mt-3">
                <button onClick={() => setStep(2)} className="flex-1 h-10 rounded-lg bg-zinc-800 text-zinc-300">Zurück</button>
                <button
                  onClick={mode === "direct" ? submitDirect : submitInvite}
                  disabled={submitting}
                  className={`flex-1 h-10 rounded-lg font-bold inline-flex items-center justify-center gap-1.5 disabled:bg-zinc-800 disabled:text-zinc-500 ${mode === "direct" ? "bg-[#00E575] text-[#09090B]" : "bg-amber-500 text-[#09090B]"}`}
                  data-testid="wizard-submit"
                >
                  {submitting ? <Loader2 size={13} className="animate-spin" /> : (mode === "direct" ? "Mandant anlegen" : "Einladung senden")}
                </button>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
